import React from "react"
import { useParams } from "react-router-dom"
import { Heart, Users, Award, Shield, Baby, Stethoscope } from "lucide-react"
import { MaternityCareLayout } from "../../components/maternity-care-layout"

const MaternityCareDetail = () => {
  const { slug } = useParams()

  const careData = {
    "prenatal-care": {
      title: "Prenatal Care",
      heroTagline: "Nurturing Your Journey to Motherhood",
      heroImage: "/images/portrait-newborn-baby-with-mother.jpg",
      highlights: [
        { icon: Heart, text: "Personalized Care Plans" },
        { icon: Users, text: "Expert Medical Team" },
        { icon: Shield, text: "24/7 Support Available" },
      ],
      sectionTitle: "Comprehensive Prenatal Care Services",
      description: "Our prenatal care program is designed to support you through every stage of pregnancy. From your first trimester to delivery, we provide comprehensive monitoring, nutritional guidance, and emotional support to ensure the health and wellbeing of both mother and baby.",
      readMoreContent: "We offer regular check-ups, ultrasound scans, genetic screening, and prenatal classes. Our multidisciplinary team includes obstetricians, midwives, nutritionists, and counselors who work together to create a personalized care plan. We also provide specialized care for high-risk pregnancies, ensuring that every mother receives the attention and expertise she needs.",
      contentImage: "/images/SecondTrimester.png",
      doctors: [
        {
          name: "Dr. Priya Sharma",
          designation: "Senior Obstetrician & Gynecologist",
          languages: "English, Hindi, Telugu",
          location: "Madinaguda",
          image: "/images/professional-female-dermatologist.jpg",
        },
        {
          name: "Dr. Anjali Reddy",
          designation: "Consultant - Maternal Fetal Medicine",
          languages: "Telugu, English",
          location: "Nallagandla",
          image: "/images/professional-female-dermatologist.jpg",
        },
        {
          name: "Dr. Kavitha Menon",
          designation: "Obstetrician & High-Risk Pregnancy Specialist",
          languages: "English, Tamil, Hindi",
          location: "Isnapur",
          image: "/images/professional-female-dermatologist.jpg",
        },
        {
          name: "Dr. Sneha Patel",
          designation: "Consultant - Obstetrics",
          languages: "English, Hindi",
          location: "Zaheerabad",
          image: "/images/professional-female-dermatologist.jpg",
        },
        {
          name: "Dr. Lakshmi Iyer",
          designation: "Prenatal Care Specialist",
          languages: "English, Telugu, Tamil",
          location: "Madinaguda",
          image: "/images/professional-female-dermatologist.jpg",
        },
        {
          name: "Dr. Divya Kumar",
          designation: "Maternal Health Consultant",
          languages: "English, Hindi",
          location: "Nallagandla",
          image: "/images/professional-female-dermatologist.jpg",
        },
      ],
      faqs: [
        {
          q: "When should I start prenatal care?",
          a: "It's recommended to schedule your first prenatal visit as soon as you confirm your pregnancy, ideally within the first 8 weeks. Early care helps identify any potential risks and establishes a healthy foundation for your pregnancy.",
        },
        {
          q: "How often will I need prenatal check-ups?",
          a: "Typically, you'll have monthly visits until 28 weeks, bi-weekly visits until 36 weeks, and weekly visits until delivery. High-risk pregnancies may require more frequent monitoring.",
        },
        {
          q: "What tests are included in prenatal care?",
          a: "Standard tests include blood work, urine tests, ultrasounds, glucose screening, and genetic testing. Your doctor will recommend specific tests based on your individual needs and pregnancy stage.",
        },
        {
          q: "Do you offer prenatal classes?",
          a: "Yes, we offer comprehensive prenatal classes covering childbirth preparation, breastfeeding, newborn care, and postpartum recovery. Classes are available both in-person and online.",
        },
      ],
    },
    "postnatal-care": {
      title: "Postnatal Care",
      heroTagline: "Supporting Your Recovery & New Beginning",
      heroImage: "/images/portrait-newborn-baby-with-mother.jpg",
      highlights: [
        { icon: Baby, text: "Newborn Care Support" },
        { icon: Stethoscope, text: "Postpartum Monitoring" },
        { icon: Heart, text: "Breastfeeding Guidance" },
      ],
      sectionTitle: "Complete Postnatal Care & Recovery",
      description: "The postpartum period is crucial for both mother and baby. Our postnatal care services provide comprehensive support during your recovery, including physical check-ups, mental health support, breastfeeding assistance, and newborn care guidance to help you navigate this transformative time with confidence.",
      readMoreContent: "We monitor your physical recovery, provide lactation support, screen for postpartum depression, and offer guidance on infant care, nutrition, and family planning. Our team is available for home visits and 24/7 consultation to address any concerns. We also conduct newborn health assessments, vaccination schedules, and developmental milestone tracking.",
      contentImage: "/images/SecondTrimester.png",
      doctors: [
        {
          name: "Dr. Meera Nair",
          designation: "Postpartum Care Specialist",
          languages: "English, Malayalam, Hindi",
          location: "Madinaguda",
          image: "/images/professional-female-dermatologist.jpg",
        },
        {
          name: "Dr. Radhika Joshi",
          designation: "Consultant - Postnatal & Lactation",
          languages: "English, Hindi, Marathi",
          location: "Nallagandla",
          image: "/images/professional-female-dermatologist.jpg",
        },
        {
          name: "Dr. Shalini Rao",
          designation: "Maternal Health & Recovery Specialist",
          languages: "English, Telugu, Kannada",
          location: "Isnapur",
          image: "/images/professional-female-dermatologist.jpg",
        },
        {
          name: "Dr. Pooja Desai",
          designation: "Postpartum Wellness Consultant",
          languages: "English, Hindi, Gujarati",
          location: "Zaheerabad",
          image: "/images/professional-female-dermatologist.jpg",
        },
        {
          name: "Dr. Nandini Singh",
          designation: "Neonatologist & Postnatal Care",
          languages: "English, Hindi",
          location: "Madinaguda",
          image: "/images/professional-female-dermatologist.jpg",
        },
        {
          name: "Dr. Asha Verma",
          designation: "Lactation Consultant & Pediatrician",
          languages: "English, Hindi, Punjabi",
          location: "Nallagandla",
          image: "/images/professional-female-dermatologist.jpg",
        },
      ],
      faqs: [
        {
          q: "How long does postpartum recovery take?",
          a: "Physical recovery typically takes 6-8 weeks, but complete recovery can take several months. Every woman's experience is different, and we provide personalized support throughout your journey.",
        },
        {
          q: "What warning signs should I watch for after delivery?",
          a: "Contact us immediately if you experience heavy bleeding, severe pain, fever, difficulty breathing, chest pain, or signs of infection. We're available 24/7 for emergencies.",
        },
        {
          q: "Do you provide breastfeeding support?",
          a: "Yes, our certified lactation consultants offer one-on-one support, troubleshooting, and guidance for successful breastfeeding. We also provide support for mothers who choose formula feeding.",
        },
        {
          q: "When should I schedule my postpartum check-up?",
          a: "Your first check-up should be within 1-2 weeks after delivery, with a comprehensive exam at 6 weeks postpartum. We'll monitor your recovery and address any concerns.",
        },
      ],
    },
  }

  const data = careData[slug] || careData["prenatal-care"]

  return <MaternityCareLayout {...data} />
}

export default MaternityCareDetail
